function safeText(value) {
  return String(value || '').trim();
}

function normalizeMatchText(value) {
  return safeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function extractMatterIdFromHref(href = '') {
  const candidate = safeText(href);
  if (!candidate) return '';

  try {
    const url = new URL(candidate, 'https://advoq.local');
    const queryMatterId = safeText(url.searchParams.get('matter_id'));
    if (queryMatterId) return queryMatterId;
    const routeMatch = url.pathname.match(/^\/(?:war-room|case|aina|red-team)\/([^/?#]+)/i);
    return safeText(safeDecode(routeMatch?.[1] || ''));
  } catch {
    const queryMatch = candidate.match(/[?&]matter_id=([^&#]+)/i);
    if (queryMatch?.[1]) return safeText(safeDecode(queryMatch[1]));
    const routeMatch = candidate.match(/^\/(?:war-room|case|aina|red-team)\/([^/?#]+)/i);
    return safeText(safeDecode(routeMatch?.[1] || ''));
  }
}

function inferContinueKind(item = {}) {
  const explicit = safeText(item.kind).toUpperCase();
  if (explicit) return explicit;

  const href = safeText(item.href);
  if (href.startsWith('/documents') || href.startsWith('/drafting')) return 'DOCUMENT';
  if (href.startsWith('/chat')) return 'CHAT';
  if (href.startsWith('/research')) return 'RESEARCH';
  if (href.startsWith('/jolly') || href.startsWith('/voice-notes')) return 'JOLLY';
  if (href.startsWith('/aina') || href.startsWith('/red-team')) return 'AINA';
  if (href.startsWith('/war-room') || href.startsWith('/case')) return 'MATTER';
  return 'MATTER';
}

function normalizeContinueItem(item = {}, index = 0) {
  const href = safeText(item.href) || '/war-room';
  const matterId = safeText(item.matter_id)
    || extractMatterIdFromHref(href)
    || (safeText(item.id).startsWith('matter:') ? safeText(item.id).slice('matter:'.length) : '');

  return {
    ...item,
    id: safeText(item.id) || `continue_${index + 1}`,
    kind: inferContinueKind(item),
    title: safeText(item.title) || 'Resume work',
    summary: safeText(item.summary) || 'Continue where you left off.',
    href,
    cta: safeText(item.cta) || 'Continue',
    eyebrow: safeText(item.eyebrow),
    matter_id: matterId,
    matter_name: safeText(item.matter_name),
  };
}

function matchesMatterName(item = {}, matterName = '') {
  const normalizedMatterName = normalizeMatchText(matterName);
  if (!normalizedMatterName) return false;

  return [
    item.matter_name,
    item.title,
    item.summary,
  ].some((value) => normalizeMatchText(value) === normalizedMatterName);
}

export function resolveMatterResumeTarget({ matter = {}, continueItems = [] } = {}) {
  const matterId = safeText(matter.matter_id);
  const matterName = safeText(matter.title || matter.name || matter.matter_name || matterId);
  const fallbackHref = safeText(matter.resume_href || matter.route) || (matterId ? `/war-room/${matterId}` : '/war-room');
  const normalizedItems = Array.isArray(continueItems)
    ? continueItems.map(normalizeContinueItem)
    : [];

  const matchedItem = normalizedItems.find((item) => (
    (item.matter_id && matterId && item.matter_id === matterId)
    || matchesMatterName(item, matterName)
  ));

  return {
    href: safeText(matchedItem?.href) || fallbackHref,
    cta: safeText(matchedItem?.cta) || 'Continue',
    summary: safeText(matchedItem?.summary)
      || safeText(matter.next_step || matter.suggested_focus || matter.recommended_action || matter.overview || matter.summary)
      || 'Continue this matter.',
    kind: safeText(matchedItem?.kind) || 'MATTER',
    eyebrow: safeText(matchedItem?.eyebrow) || 'Resume matter',
    matched: Boolean(matchedItem),
    matched_item: matchedItem || null,
    route: fallbackHref,
  };
}

export function buildFallbackMatterDesk(payload = {}) {
  const matter = payload?.matter || {};
  const caseRecord = payload?.case || {};
  const matterId = safeText(matter.matter_id);
  const matterName = safeText(matter.title || matter.name || matterId);
  const workspaceId = safeText(caseRecord.workspace_id || matter.case_workspace_id);
  const params = new URLSearchParams();
  if (matterId) params.set('matter_id', matterId);
  if (matterName) params.set('matter_name', matterName);
  if (workspaceId) params.set('workspace_id', workspaceId);
  const chatResearchQuery = params.toString();

  const matterParams = new URLSearchParams();
  if (matterId) matterParams.set('matter_id', matterId);
  if (matterName) matterParams.set('matter_name', matterName);

  return {
    matter_id: matterId,
    matter_name: matterName,
    workspace_id: workspaceId,
    current_objective: {
      text: safeText(matter.case_current_objective || caseRecord.summary || matter.description),
      source: 'fallback',
    },
    routes: {
      hearing: matterId ? `/case/${matterId}#hearing-prep` : '/case',
      case: matterId ? `/war-room/${matterId}` : '/war-room',
      sabha: matterId ? `/war-room/${matterId}` : '/war-room',
      ops: matterId ? `/war-room/${matterId}?view=ops` : '/war-room?view=ops',
      bench: matterId ? `/war-room/${matterId}?view=bench` : '/war-room?view=bench',
      agents: matterId ? `/war-room/${matterId}?view=agents` : '/war-room?view=agents',
      chat: chatResearchQuery ? `/chat?${chatResearchQuery}` : '/chat',
      research: chatResearchQuery ? `/research?${chatResearchQuery}` : '/research',
      jolly: matterParams.toString() ? `/jolly?${matterParams.toString()}` : '/jolly',
      aina: matterId ? `/aina/${matterId}${workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : ''}` : '/aina',
    },
    operations: {
      headline: 'Matter Ops keeps court watch, filing execution, team follow-through, and client updates inside the same matter.',
      state: {
        court_watch: { tracking_mode: 'manual', notes: '', items: [] },
        filing_factory: { target_filing: '', bundle_status: 'scoping', notes: '' },
        team_tasks: [],
        client_requests: [],
        notes: '',
      },
      court_watch: {
        tracking_mode: 'manual',
        status_label: 'Manual watch',
        sync_note: 'Add precise case metadata to make court-watch useful beyond manual notes.',
        official_channels: [],
        tracking_items: [],
        notes: '',
        leading_issue: '',
      },
      filing_factory: {
        target_filing: '',
        bundle_status: 'scoping',
        bundle_label: 'Scoping',
        checklist: [],
        annexures: [],
        defect_radar: [],
        output_bundle: [],
        notes: '',
      },
      team_coordination: {
        tasks: [],
        review_queue: [],
        summary: '',
        counts: {},
      },
      client_loop: {
        headline: '',
        summary: '',
        update_draft: { headline: '', summary: '', bullets: [] },
        requests: [],
        channel_suggestion: '',
        bilingual_ready: false,
      },
      playbook: {
        label: '',
        phase_label: '',
        target_filing: '',
        now: [],
        next: [],
        watchouts: [],
      },
      signals: {},
    },
    selected_context: [],
    readiness: {
      label: 'Building',
      missing: [],
    },
    next_steps: [],
  };
}

export function resolveMatterDesk(payload = {}) {
  return payload?.matterDesk || buildFallbackMatterDesk(payload);
}

export function getMatterDeskRoute(matterDesk, target, fallback = '/') {
  return matterDesk?.routes?.[target] || fallback;
}

export function buildSentinelMatterContext(matterDesk, payload = {}) {
  const matter = payload?.matter || {};
  return {
    matterId: matterDesk?.matter_id || matter?.matter_id || '',
    matterName: matterDesk?.matter_name || matter?.title || matter?.name || matter?.matter_id || '',
    workspaceId: matterDesk?.workspace_id || payload?.case?.workspace_id || matter?.case_workspace_id || '',
    objective: matterDesk?.current_objective?.text || '',
  };
}
